// src/pages/Notes/NoteView.jsx
// STATUS: active | UPDATED: 2026-09-17
// LOG:
//   2026-09-17  Typography for the sheet. Tyler asked for the notes to give
//               Jazz vision, and a wall of default paragraphs does not.
//   2026-09-17  Attachments, so an updated version of a document can be
//               replied onto the original rather than emailed.
//   2026-09-17  New. One note, its thread, and the four things he can do
//               with it.
//
// body_html is the same sheet that prints to the PDF, rendered rather than
// attached. It is injected, which is only acceptable because the notes table
// is level 00 write and every row in it is authored by us. If that audience
// ever widens past admin, this has to be sanitised first. Migration 0028 has
// the audience column precisely so that widening is a decision somebody makes
// on purpose.

import { useCallback, useEffect, useRef, useState } from 'react'
import {
  Box, Button, HStack, VStack, Text, Badge, Spinner, Alert, AlertIcon, Textarea, Divider,
} from '@chakra-ui/react'
import { Link as RouterLink, useParams } from 'react-router-dom'
import { ChevronLeft, Paperclip, X } from 'lucide-react'
import { getNote, listEntries, markRead, reply, moveTo, uploadFile, signedUrl, getAgent, STAGE_COPY } from '../../lib/notes'
import { useAuth } from '../../hooks/useAuth'

function when(iso) {
  if (!iso) return ''
  return new Date(iso).toLocaleString(undefined, {
    month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
  })
}

function Entry({ entry }) {
  // A stage move reads as a quiet line in the history, not as somebody talking.
  if (entry.kind === 'system') {
    return (
      <HStack spacing={3} py={3} color="inkDim" fontSize="sm">
        <Box flex={1} borderTop="1px solid" borderColor="line" />
        <Text>{entry.body}</Text>
        <Text>{when(entry.created_at)}</Text>
      </HStack>
    )
  }
  return (
    <Box py={4} borderTop="1px solid" borderColor="line">
      <HStack spacing={2} color="inkDim" fontSize="sm" mb={1}>
        <Text fontWeight={600} color="inkMuted">
          {entry.author_name || entry.author}
        </Text>
        <Text>{when(entry.created_at)}</Text>
      </HStack>
      {entry.body && (
        <Text fontSize="body" whiteSpace="pre-wrap">
          {entry.body}
        </Text>
      )}
      {(entry.attachments || []).length > 0 && (
        <VStack align="stretch" spacing={1.5} mt={entry.body ? 3 : 0}>
          {entry.attachments.map((f) => (
            <Attachment key={f.path} file={f} />
          ))}
        </VStack>
      )}
    </Box>
  )
}

function kb(bytes) {
  if (!bytes) return ''
  return bytes > 1024 * 1024 ? `${(bytes / 1024 / 1024).toFixed(1)}MB` : `${Math.round(bytes / 1024)}KB`
}

// The bucket is private, so the link is minted on click rather than stored.
// A signed URL pasted into a note would expire and look broken.
function Attachment({ file }) {
  const [busy, setBusy] = useState(false)
  const open = async () => {
    setBusy(true)
    const url = await signedUrl(file.path)
    setBusy(false)
    if (url) window.open(url, '_blank', 'noopener,noreferrer')
  }
  return (
    <HStack
      as="button"
      type="button"
      onClick={open}
      spacing={2.5}
      px={3}
      py={2}
      borderRadius="md"
      border="1px solid"
      borderColor="line"
      w="fit-content"
      maxW="100%"
      _hover={{ borderColor: 'inkDim' }}
    >
      <Box color="inkDim" flexShrink={0}>
        <Paperclip size={14} />
      </Box>
      <Text fontSize="body-sm" noOfLines={1}>
        {file.name}
      </Text>
      <Text fontSize="sm" color="inkDim" flexShrink={0}>
        {busy ? 'opening' : kb(file.size)}
      </Text>
    </HStack>
  )
}

export default function NoteView() {
  const { slug } = useParams()
  const { user, profile } = useAuth()
  const displayName = profile?.display_name || profile?.first_name || ''
  const [state, setState] = useState({ loading: true, note: null, entries: [], error: null })
  const [draft, setDraft] = useState('')
  const [busy, setBusy] = useState(null)
  const [pending, setPending] = useState([])
  const [agent, setAgent] = useState(null)
  const fileRef = useRef(null)

  const load = useCallback(async () => {
    const { data: note, error } = await getNote(slug)
    if (error || !note) {
      setState({ loading: false, note: null, entries: [], error: error ? error.message : 'No note here.' })
      return
    }
    const { data: entries } = await listEntries(note.id)
    setState({ loading: false, note, entries, error: null })

    // Read receipt, once, after it is on screen.
    const { data: seen } = await markRead(note, user?.id)
    if (seen) setState((s) => ({ ...s, note: { ...s.note, ...seen } }))
  }, [slug, user?.id])

  useEffect(() => {
    load()
  }, [load])

  useEffect(() => {
    let alive = true
    getAgent().then((a) => alive && setAgent(a))
    return () => {
      alive = false
    }
  }, [])

  const attach = async (e) => {
    const files = Array.from(e.target.files || [])
    e.target.value = ''
    if (files.length === 0) return
    setBusy('upload')
    for (const file of files) {
      const { data, error } = await uploadFile(state.note.id, file)
      if (data) setPending((p) => [...p, data])
      else setState((s) => ({ ...s, error }))
    }
    setBusy(null)
  }

  const send = async () => {
    setBusy('reply')
    const { error } = await reply({
      noteId: state.note.id,
      userId: user?.id,
      authorName: displayName,
      body: draft,
      attachments: pending,
    })
    setBusy(null)
    if (!error) {
      setDraft('')
      setPending([])
      load()
    }
  }

  const move = async (stage) => {
    setBusy(stage)
    await moveTo({ note: state.note, stage, userId: user?.id, authorName: displayName })
    setBusy(null)
    load()
  }

  if (state.loading) {
    return (
      <HStack spacing={3} color="inkMuted">
        <Spinner size="sm" />
        <Text fontSize="body">Loading</Text>
      </HStack>
    )
  }

  if (state.error) {
    return (
      <Alert status="error" borderRadius="md" maxW="sheet">
        <AlertIcon />
        {state.error}
      </Alert>
    )
  }

  const { note } = state
  const stage = STAGE_COPY[note.stage] || { label: note.stage, hint: '' }

  return (
    <VStack align="stretch" spacing={7} maxW="sheet">
      <HStack
        as={RouterLink}
        to="/notes/"
        spacing={2}
        color="inkMuted"
        fontSize="body-sm"
        alignSelf="flex-start"
        _hover={{ textDecoration: 'none', color: 'ink' }}
      >
        <ChevronLeft size={15} />
        <Text>All notes</Text>
      </HStack>

      <Box>
        {note.kicker && (
          <Text textStyle="pageEyebrow" mb={1}>
            {note.kicker}
          </Text>
        )}
        <Text textStyle="pageTitle">{note.title}</Text>
        <HStack spacing={3} mt={2} color="inkDim" fontSize="sm" flexWrap="wrap">
          <Badge variant="subtle">{stage.label}</Badge>
          {agent && note.author === 'coach' && <Text>Written by {agent.name}</Text>}
          <Text>{stage.hint}</Text>
        </HStack>
      </Box>

      {note.summary && (
        <Text fontSize="lg" color="inkMuted" lineHeight={1.55} maxW="measure">
          {note.summary}
        </Text>
      )}

      {/* The sheet itself. Authored by us, level 00 only. See the header note. */}
      {note.body_html && (
        <Box
          className="note-sheet"
          sx={{
            // Every colour here comes from a theme token, never a literal. A
            // note is authored content injected into a themed app, and a hex
            // code in the HTML is a note that looks correct until somebody
            // switches to dark and then looks broken.
            h2: {
              fontWeight: 600,
              fontSize: 'xl',
              letterSpacing: '-0.015em',
              lineHeight: 1.2,
              mt: 11,
              mb: 3,
              pt: 5,
              borderTop: '1px solid',
              borderColor: 'line',
              maxW: 'measure',
            },
            'h2:first-of-type': { mt: 2, pt: 0, borderTop: 'none' },
            h3: { fontWeight: 600, fontSize: 'md', letterSpacing: '-0.01em', mt: 7, mb: 1.5 },
            p: { mb: 4, maxW: 'measure', lineHeight: 1.68 },
            // The opening paragraph carries the weight, so it gets the size.
            'p.lede': { fontSize: 'lg', lineHeight: 1.5, color: 'inkMuted', mb: 6 },
            // Something of his, or something that needs to stop the eye.
            'p.callout, blockquote': {
              borderLeft: '3px solid',
              borderColor: 'accent',
              pl: 5,
              py: 1,
              my: 6,
              fontSize: 'lg',
              lineHeight: 1.5,
              maxW: 'measure',
            },
            'ul, ol': { mb: 5, pl: 0, maxW: 'measure', listStyle: 'none' },
            li: { mb: 2.5, pl: 6, position: 'relative', lineHeight: 1.6 },
            'ul li::before': {
              content: '""',
              position: 'absolute',
              left: '6px',
              top: '0.62em',
              w: '5px',
              h: '5px',
              bg: 'accent',
              borderRadius: '1px',
            },
            'ol': { counterReset: 'n' },
            'ol li': { counterIncrement: 'n' },
            'ol li::before': {
              content: 'counter(n)',
              position: 'absolute',
              left: 0,
              top: 0,
              fontSize: 'sm',
              color: 'inkDim',
              fontVariantNumeric: 'tabular-nums',
            },
            // Tables scroll on a phone rather than squeezing the page sideways.
            '.tablewrap': { overflowX: 'auto', mb: 6 },
            table: { width: '100%', minW: '420px', borderCollapse: 'collapse', mb: 6 },
            'th, td': {
              textAlign: 'left',
              verticalAlign: 'top',
              py: 3.5,
              pr: 5,
              borderBottom: '1px solid',
              borderColor: 'line',
              lineHeight: 1.55,
            },
            'td:last-of-type, th:last-of-type': { pr: 0 },
            th: {
              fontSize: 'xs',
              letterSpacing: '0.1em',
              textTransform: 'uppercase',
              color: 'inkDim',
              fontWeight: 500,
              py: 2,
              borderBottom: '1px solid',
              borderColor: 'line',
            },
            'td:first-of-type': { fontWeight: 600, whiteSpace: 'normal' },
            strong: { fontWeight: 600 },
          }}
          dangerouslySetInnerHTML={{ __html: note.body_html }}
        />
      )}

      <Divider borderColor="line" />

      <Box>
        <Text fontSize="lg" fontWeight={600} mb={1}>
          What do you think?
        </Text>
        <Text fontSize="body" color="inkMuted" mb={4} maxW="measure">
          Anything at all. An idea, a correction, or tell us to go build it.
        </Text>

        {state.entries.map((e) => (
          <Entry key={e.id} entry={e} />
        ))}

        <VStack align="stretch" spacing={3} mt={5}>
          <Textarea
            id="note-reply"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder="Type or dictate"
            rows={4}
            fontSize="body"
          />
          {pending.length > 0 && (
            <VStack align="stretch" spacing={1.5}>
              {pending.map((f) => (
                <HStack key={f.path} spacing={2} px={3} py={2} borderRadius="md" bg="card">
                  <Box color="inkDim">
                    <Paperclip size={14} />
                  </Box>
                  <Text fontSize="body-sm" noOfLines={1} flex={1}>
                    {f.name}
                  </Text>
                  <Box
                    as="button"
                    type="button"
                    color="inkDim"
                    onClick={() => setPending((p) => p.filter((x) => x.path !== f.path))}
                    aria-label={`Remove ${f.name}`}
                  >
                    <X size={14} />
                  </Box>
                </HStack>
              ))}
            </VStack>
          )}

          <input
            id="note-attach"
            ref={fileRef}
            type="file"
            multiple
            onChange={attach}
            style={{ display: 'none' }}
          />

          <HStack spacing={2} flexWrap="wrap">
            <Button
              size="sm"
              onClick={send}
              isLoading={busy === 'reply'}
              isDisabled={!draft.trim() && pending.length === 0}
            >
              Reply
            </Button>
            <Button
              size="sm"
              variant="outline"
              leftIcon={<Paperclip size={14} />}
              onClick={() => fileRef.current?.click()}
              isLoading={busy === 'upload'}
            >
              Attach
            </Button>
            <Button size="sm" variant="outline" onClick={() => move('approved')} isLoading={busy === 'approved'}>
              Build it
            </Button>
            <Button size="sm" variant="ghost" onClick={() => move('parked')} isLoading={busy === 'parked'}>
              Not now
            </Button>
          </HStack>
        </VStack>
      </Box>
    </VStack>
  )
}
